import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, Button, StatusBadge, toast } from '@etherisc/ui-kit';
import { JobDetail, JobStatus, ClimateJobDetailResponse, ChirpsJobOutput } from '../../types/job';
import { jobService } from '../../services/climate/job/JobService';
import { ClimateApiError } from '../../services/climate/base/ClimateApiError';
import { ChirpsJobOutputVisualization } from '../../components/job/ChirpsJobOutputVisualization';
import { ArrowLeft, RefreshCw, Play, Pause, X } from 'lucide-react';

// Simple loading spinner component
const SimpleSpinner: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div className={`animate-spin rounded-full h-4 w-4 border-b-2 border-current ${className}`}></div>
);

// Job status badge component
const JobStatusBadge: React.FC<{ status: JobStatus }> = ({ status }) => {
  const getStatusVariant = (status: JobStatus): 'success' | 'warning' | 'error' | 'info' | 'neutral' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'running':
        return 'info';
      case 'error':
      case 'failed':
      case 'timeout':
        return 'error';
      case 'cancelled':
      case 'paused':
      case 'partial':
        return 'warning';
      default:
        return 'neutral';
    }
  };

  const getStatusLabel = (status: JobStatus): string => {
    const statusMap: Record<JobStatus, string> = {
      'queued': 'Queued',
      'running': 'Running',
      'completed': 'Completed',
      'error': 'Error',
      'cancelled': 'Cancelled',
      'paused': 'Paused',
      'timeout': 'Timeout',
      'pending': 'Pending',
      'failed': 'Failed',
      'partial': 'Partial'
    };
    return statusMap[status] || status;
  };

  return (
    <StatusBadge variant={getStatusVariant(status)}>
      {getStatusLabel(status)}
    </StatusBadge>
  );
};

// Progress bar component
const JobProgressBar: React.FC<{ progress: number; status: JobStatus }> = ({ progress, status }) => {
  const getProgressColor = (status: JobStatus) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'running':
        return 'bg-blue-500';
      case 'error':
      case 'failed':
      case 'timeout':
        return 'bg-red-500';
      case 'cancelled':
      case 'paused':
        return 'bg-yellow-500';
      case 'partial':
        return 'bg-orange-500';
      default:
        return 'bg-gray-400';
    }
  };

  return (
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div
        className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(status)}`}
        style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
      />
    </div>
  );
};

// JSON display component
const JsonDisplay: React.FC<{ data: unknown; title: string }> = ({ data, title }) => {
  if (!data || (typeof data === 'object' && data !== null && Object.keys(data).length === 0)) {
    return (
      <div className="text-gray-500 text-sm">
        No {title.toLowerCase()} data available
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border rounded-lg p-4 overflow-x-auto">
      <pre className="text-xs whitespace-pre-wrap break-words">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
};

export const JobDetailPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<JobDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [controlAction, setControlAction] = useState<'cancel' | 'pause' | 'resume' | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  /**
   * Normalize job status, setting default to "failed" if no status provided
   */
  const normalizeJobStatus = (status: string | null | undefined): JobStatus => {
    const validStatuses: JobStatus[] = ['queued', 'running', 'completed', 'error', 'cancelled', 'paused', 'timeout', 'pending', 'failed', 'partial'];
    
    if (!status || !validStatuses.includes(status as JobStatus)) {
      return 'failed';
    }
    
    return status as JobStatus;
  };

  /**
   * Fetch job details from API
   */
  const fetchJobDetails = async (showRefreshLoader = false) => {
    if (!jobId) return;

    try {
      if (showRefreshLoader) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await jobService.getJobById(jobId);
      
      if (response.success) {
        // Transform the flattened response into a JobDetail object
        const jobDetail: JobDetail = {
          job_id: response.job_id,
          job_type: response.job_type,
          status: normalizeJobStatus(response.status),
          progress: response.progress,
          message: response.message,
          created_at: response.created_at,
          updated_at: response.updated_at,
          // Handle optional fields that might not be present in the response
          started_at: 'started_at' in response ? (response as ClimateJobDetailResponse & { started_at: string }).started_at : undefined,
          completed_at: 'completed_at' in response ? (response as ClimateJobDetailResponse & { completed_at: string }).completed_at : undefined,
          timeout_seconds: 'timeout_seconds' in response ? (response as ClimateJobDetailResponse & { timeout_seconds: number }).timeout_seconds : 0,
          parent_request_id: 'parent_request_id' in response ? (response as ClimateJobDetailResponse & { parent_request_id: string }).parent_request_id : undefined,
          input: response.input,
          output: response.output,
          error_details: response.error_details,
          control_flags: response.control_flags
        };
        setJob(jobDetail);
      } else {
        setError('Failed to fetch job details');
      }
    } catch (err) {
      if (err instanceof ClimateApiError) {
        setError(err.getUserMessage());
      } else {
        setError('An unexpected error occurred while fetching job details');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  /**
   * Handle back navigation
   */
  const handleBack = () => {
    navigate('/admin/jobs');
  };

  /**
   * Handle refresh
   */
  const handleRefresh = () => {
    fetchJobDetails(true);
  };

  /**
   * Handle job control actions
   */
  const handleControlAction = (action: 'cancel' | 'pause' | 'resume') => {
    setControlAction(action);
    setConfirmDialogOpen(true);
  };

  const executeControlAction = async () => {
    if (!jobId || !controlAction) return;

    setConfirmDialogOpen(false);
    
    try {
      let result;
      
      switch (controlAction) {
        case 'cancel':
          result = await jobService.cancelJob(jobId);
          break;
        case 'pause':
          result = await jobService.pauseJob(jobId);
          break;
        case 'resume':
          result = await jobService.resumeJob(jobId);
          break;
      }
      
      if (result.success) {
        toast.success(result.message || `Job ${controlAction}ed successfully`);
        // Refresh job details to show updated status
        fetchJobDetails(true);
      } else {
        toast.error(`Failed to ${controlAction} job`);
      }
    } catch (err) {
      const errorMessage = err instanceof ClimateApiError 
        ? err.message 
        : `Failed to ${controlAction} job`;
      toast.error(errorMessage);
    } finally {
      setControlAction(null);
    }
  };

  /**
   * Format timestamp for display
   * Expects ISO 8601 UTC timestamps from server (e.g., "2025-12-09T11:49:54Z")
   * Automatically converts to user's local timezone
   */
  const formatTimestamp = (timestamp: string): string => {
    return new Date(timestamp).toLocaleString();
  };

  /**
   * Get duration string
   * Expects ISO 8601 UTC timestamps from server (e.g., "2025-12-09T11:49:54Z")
   */
  const getDuration = (job: JobDetail): string => {
    if (!job.started_at) return 'Not started';
    
    const start = new Date(job.started_at);
    const end = job.completed_at ? new Date(job.completed_at) : new Date();
    const durationMs = end.getTime() - start.getTime();
    
    const hours = Math.floor(durationMs / 3600000);
    const minutes = Math.floor((durationMs % 3600000) / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  // Load job details on mount
  useEffect(() => {
    fetchJobDetails();
  }, [jobId]);

  // Handle missing job ID
  if (!jobId) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="text-red-500 text-lg mb-2">Invalid Job ID</div>
          <Button onClick={handleBack} intent="primary">
            Back to Job List
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            onClick={handleBack}
            intent="outline"
            size="sm"
            className="flex items-center space-x-2"
          >
            <ArrowLeft size={16} />
            <span>Back to Jobs</span>
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Job Details</h1>
            <p className="text-gray-600 mt-1 font-mono text-sm">{jobId}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            onClick={handleRefresh}
            disabled={refreshing}
            intent="outline"
            size="sm"
            className="flex items-center space-x-2"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </Button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <SimpleSpinner className="h-8 w-8" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error Loading Job Details</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <Button onClick={() => fetchJobDetails()} intent="outline">
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Job Details */}
      {job && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <div className="mt-1">
                    <JobStatusBadge status={job.status} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Type</label>
                  <div className="mt-1 text-sm">{String(job.job_type)}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Progress</label>
                  <div className="mt-1">
                    <JobProgressBar progress={job.progress} status={job.status} />
                    <div className="text-sm text-gray-600 mt-1">{job.progress}%</div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Duration</label>
                  <div className="mt-1 text-sm">{getDuration(job)}</div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Message</label>
                <div className="mt-1 text-sm bg-gray-50 p-2 rounded border">
                  {job.message || 'No message'}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Timestamps */}
          <Card>
            <CardHeader>
              <CardTitle>Timeline</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Created</label>
                  <div className="mt-1 text-sm">{formatTimestamp(job.created_at)}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Updated</label>
                  <div className="mt-1 text-sm">{formatTimestamp(job.updated_at)}</div>
                </div>
                {job.started_at && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Started</label>
                    <div className="mt-1 text-sm">{formatTimestamp(job.started_at)}</div>
                  </div>
                )}
                {job.completed_at && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Completed</label>
                    <div className="mt-1 text-sm">{formatTimestamp(job.completed_at)}</div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Timeout</label>
                  <div className="mt-1 text-sm">{job.timeout_seconds || 0}s</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Control Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Control Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex space-x-2">
                <Button
                  onClick={() => handleControlAction('cancel')}
                  disabled={job.status !== 'running' && job.status !== 'queued' && job.status !== 'paused'}
                  intent="outline"
                  size="sm"
                  className="flex items-center space-x-2"
                  title={job.status === 'running' || job.status === 'queued' || job.status === 'paused' ? 'Cancel this job' : 'Job cannot be cancelled'}
                >
                  <X size={16} />
                  <span>Cancel</span>
                </Button>
                <Button
                  onClick={() => handleControlAction('pause')}
                  disabled={job.status !== 'running'}
                  intent="outline"
                  size="sm"
                  className="flex items-center space-x-2"
                  title={job.status === 'running' ? 'Pause this job' : 'Only running jobs can be paused'}
                >
                  <Pause size={16} />
                  <span>Pause</span>
                </Button>
                <Button
                  onClick={() => handleControlAction('resume')}
                  disabled={job.status !== 'paused'}
                  intent="outline"
                  size="sm"
                  className="flex items-center space-x-2"
                  title={job.status === 'paused' ? 'Resume this job' : 'Only paused jobs can be resumed'}
                >
                  <Play size={16} />
                  <span>Resume</span>
                </Button>
              </div>
              
              {/* Control Flags */}
              <div className="space-y-2">
                <h4 className="font-medium text-sm">Control Flags</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${job.control_flags.cancel_requested ? 'bg-red-500' : 'bg-gray-300'}`}></div>
                    <span>Cancel Requested</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${job.control_flags.pause_requested ? 'bg-yellow-500' : 'bg-gray-300'}`}></div>
                    <span>Pause Requested</span>
                  </div>
                </div>
                <div className="text-xs text-gray-600">
                  Timeout: {formatTimestamp(job.control_flags.timeout_at)}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Input Parameters */}
          <Card>
            <CardHeader>
              <CardTitle>Input Parameters</CardTitle>
            </CardHeader>
            <CardContent>
              <JsonDisplay data={job.input} title="Input" />
            </CardContent>
          </Card>

          {/* Output Results */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Job Details</CardTitle>
            </CardHeader>
            <CardContent>
              {job.job_type === 'data_intake' && job.output && typeof job.output === 'object' && job.output !== null ? (
                // Check if the output has the CHIRPS structure
                'summary' in job.output ? (
                  <ChirpsJobOutputVisualization output={job.output as unknown as ChirpsJobOutput} />
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>Data intake job output structure not recognized.</p>
                    <details className="mt-4 text-left">
                      <summary className="cursor-pointer text-blue-600">Show raw output</summary>
                      <JsonDisplay data={job.output} title="Output" />
                    </details>
                  </div>
                )
              ) : job.job_type === 'data_intake' ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No job details provided for this data intake job.</p>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No job details provided.</p>
                  {job.output && typeof job.output === 'object' && job.output !== null && (
                    <details className="mt-4 text-left">
                      <summary className="cursor-pointer text-blue-600">Show raw output</summary>
                      <JsonDisplay data={job.output} title="Output" />
                    </details>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Error Details */}
          {job.error_details && typeof job.error_details === 'object' && job.error_details !== null ? (
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-red-700">Error Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <JsonDisplay data={job.error_details} title="Error" />
                </div>
              </CardContent>
            </Card>
          ) : null}
        </div>
      )}

      {/* Confirmation Dialog for Job Control Actions */}
      {confirmDialogOpen && controlAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Confirm {controlAction.charAt(0).toUpperCase() + controlAction.slice(1)} Job
                </h3>
                <p className="text-sm text-gray-600">
                  {controlAction === 'cancel' && 'Are you sure you want to cancel this job? This action cannot be undone and the job will stop immediately.'}
                  {controlAction === 'pause' && 'Are you sure you want to pause this job? You can resume it later from where it stopped.'}
                  {controlAction === 'resume' && 'Are you sure you want to resume this job? It will continue from where it was paused.'}
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="text-sm">
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-700">Job ID:</span>
                    <span className="font-mono text-xs text-gray-900">{jobId?.slice(0, 12)}...</span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-700">Type:</span>
                    <span className="font-medium text-gray-900">{job?.job_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Current Status:</span>
                    <span className="font-medium text-gray-900">{job?.status}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 justify-end">
                <Button
                  onClick={() => {
                    setConfirmDialogOpen(false);
                    setControlAction(null);
                  }}
                  intent="outline"
                  size="sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={executeControlAction}
                  intent="primary"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  {controlAction === 'cancel' && <X size={16} />}
                  {controlAction === 'pause' && <Pause size={16} />}
                  {controlAction === 'resume' && <Play size={16} />}
                  <span>Confirm {controlAction.charAt(0).toUpperCase() + controlAction.slice(1)}</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 